import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import { cacheShortlistedJourneys } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ShortlistedFlightsSection/shortlistedJourneyCache'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'
import ModalContext from 'contexts/ModalContext'
import JourneyV2DetailsModal from 'components/Flights/V2/FareFamily/JourneyV2DetailsModal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import FlightSearchWidgetDesktop from 'components/Flights/FlightsSearch/SearchWidget/FlightSearchWidgetDesktop'
import useSearchFlights from 'hooks/useSearchFlights'
import type { FlightSearchParams } from 'hooks/useSearchFlights'
import { FlightSegment, FlightSortOptions, FlightsFareTypes, fareClassToUrlMap, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { INTL_DAY_MONTH_NAME, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { resetFlightsTimer, hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import DrawerFlightCard from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/FlightSearchDrawer/DrawerFlightCard'
import FlightSearchDrawer from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/FlightSearchDrawer/FlightSearchDrawer'
import Group from 'components/utils/Group'
import StepperProgressSteps from 'LuxKit/Steppers/StepperProgressSteps'
import StepperProgressStep from 'LuxKit/Steppers/StepperProgressStep'
import type { StepClickHandler } from 'LuxKit/Steppers/StepperProgressStep'
import FlightsLoadingTopBar from 'components/Flights/V2/FlightsLoadingTopBar'
import LoadingAnimationBox from 'components/Common/LoadingAnimationBox/LoadingAnimationBox'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getTotalStopCount } from 'lib/flights/flightUtils'
import { optionToJourney } from './chatFlightTypes'
import type { FlightUIData, FlightOption } from './chatFlightTypes'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import ChatFlightResultsHeader from './Flights/ChatFlightResultsHeader'
import useChatFlightResultsSorting from './Flights/useChatFlightResultsSorting'

interface Props {
  data: FlightUIData
  onSendMessage?: (message: string) => void
}

const SearchWrapper = styled.div`
  padding: ${rem(12)} ${rem(16)};
  width: 100%;
  box-sizing: border-box;
`

const FlightSearchWidgetWrapper = styled.div`
  width: 100%;

  ${mediaQueryUp.tablet} {
    form {
      grid-template-areas:
        "location-input location-input"
        "date-input actions";
      grid-template-columns: minmax(0, 1fr) ${rem(112)};
    }

    form > .location-input-area,
    form > .date-input-area {
      min-width: 0;
    }
  }
`

const ContentWrapper = styled.div`
  padding: ${rem(16)};
  width: 100%;
  box-sizing: border-box;
`

const FlightResultsViewport = styled.div<{ $maxHeight?: number; $isScrollable: boolean }>`
  position: relative;
  overflow-x: hidden;
  overflow-y: ${({ $isScrollable }) => $isScrollable ? 'auto' : 'hidden'};
  max-height: ${({ $maxHeight }) => $maxHeight ? rem($maxHeight) : 'none'};
  overscroll-behavior: contain;
`

const FlightResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const FLIGHT_RESULTS_GAP = 12
const MAX_VISIBLE_FLIGHT_COUNT = 6
const FLIGHT_RESULTS_SCROLL_PEEK_HEIGHT = 36
const FLIGHT_RESULTS_FALLBACK_CARD_HEIGHT = 112

const EMPTY_SEARCH_PARAMS: FlightSearchParams = {
  itinerary: [{ departureAirport: '', arrivalAirport: '', departureDate: '' }],
  occupancies: [{ adults: 1 }],
  fareClass: 'economy',
}

const MAX_TIMEOUT_MS = 2147483647

const EMPTY_FLIGHT_OPTIONS: Array<FlightOption> = []

function ChatFlightSearchContent({ data, onSendMessage }: Props) {
  const { isStreaming } = useChatStreamStatus()
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const showModal = useContext(ModalContext)

  const [searchParams, setSearchParams] = useState<FlightSearchParams | null>(null)
  const [searchVersion, setSearchVersion] = useState(0)
  const [activeSegmentKey, setActiveSegmentKey] = useState<FlightSegment>(FlightSegment.DEPARTING)
  const [flightSort, setFlightSort] = useState<FlightSortOptions>(FlightSortOptions.Best)
  const [selectedFlights, setSelectedFlights] = useState<Record<string, App.JourneyV2>>({})
  const [selectedFareFamilies, setSelectedFareFamilies] = useState<Record<string, App.FlightFareFamily>>({})
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [flightCardHeight, setFlightCardHeight] = useState(0)
  const flightResultsListRef = useRef<HTMLDivElement | null>(null)

  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])

  const widgetState = useContext(FlightSearchWidgetStateContext)
  const { flights, occupants, fareType, fareCabin } = widgetState

  const isReturn = fareType?.value === FlightsFareTypes.RETURN
  const segments = useMemo<Array<FlightSegment>>(() => isReturn ? [FlightSegment.DEPARTING, FlightSegment.RETURNING] : [FlightSegment.DEPARTING], [isReturn])
  const isReturningStep = activeSegmentKey === FlightSegment.RETURNING
  const hasSearched = searchParams !== null

  const outboundOptions = data.outboundOptions ?? EMPTY_FLIGHT_OPTIONS
  const inboundOptions = data.inboundOptions ?? EMPTY_FLIGHT_OPTIONS

  const initialDepartingJourneys = useMemo(() => {
    return outboundOptions.map((opt, i) =>
      optionToJourney(opt, `departing-${i}`, opt.currencyCode ?? data.currencyCode),
    )
  }, [outboundOptions, data.currencyCode])

  const initialReturningJourneys = useMemo(() => {
    return inboundOptions.map((opt, i) =>
      optionToJourney(opt, `returning-${i}`, opt.currencyCode ?? data.currencyCode),
    )
  }, [inboundOptions, data.currencyCode])

  const { journeys: searchedJourneys, fetching, error, searchResultsTimestamp } = useSearchFlights(
    searchParams ?? EMPTY_SEARCH_PARAMS,
    {
      disabled: !searchParams,
      segments,
      activeSegmentKey,
      selectedFlights,
      selectedFareFamilies,
    },
  )

  const departDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkinDate ? moment(flight.checkinDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const returnDate = useMemo(() => {
    const flight = flights[0]
    return flight?.checkoutDate ? moment(flight.checkoutDate).format(ISO_DATE_FORMAT) : undefined
  }, [flights])

  const unsortedDisplayJourneys = useMemo<Array<App.JourneyV2>>(() => {
    if (!hasSearched) {
      return isReturningStep ? initialReturningJourneys : initialDepartingJourneys
    }

    return searchedJourneys
  }, [hasSearched, isReturningStep, initialDepartingJourneys, initialReturningJourneys, searchedJourneys])

  const displayJourneys = useChatFlightResultsSorting({
    journeys: unsortedDisplayJourneys,
    activeSegmentKey,
    sort: flightSort,
    departDate,
    returnDate,
    selectedFlights,
  })

  const handleSearch = useCallback(() => {
    const flight = flights[0]
    if (!flight.departureAirport?.airportCode || !flight.arrivalAirport?.airportCode || !flight.checkinDate) {
      return
    }

    const itinerary = [{
      departureAirport: flight.departureAirport.airportCode,
      arrivalAirport: flight.arrivalAirport.airportCode,
      departureDate: moment(flight.checkinDate).format(ISO_DATE_FORMAT),
    }]

    if (fareType?.value === FlightsFareTypes.RETURN && flight.checkoutDate) {
      itinerary.push({
        departureAirport: flight.arrivalAirport.airportCode,
        arrivalAirport: flight.departureAirport.airportCode,
        departureDate: moment(flight.checkoutDate).format(ISO_DATE_FORMAT),
      })
    }

    const nextVersion = searchVersion + 1
    setSearchVersion(nextVersion)
    setSearchParams({
      itinerary,
      occupancies: [occupants],
      fareClass: fareCabin?.value ? fareClassToUrlMap[fareCabin.value] ?? 'economy' : 'economy',
      fareType: fareType?.value,
      key: nextVersion,
    })
    setActiveSegmentKey(FlightSegment.DEPARTING)
    setSelectedFlights({})
    setSelectedFareFamilies({})
  }, [flights, occupants, fareType, fareCabin, searchVersion])

  const initialExpiryTimestamp = data.searchExpiry ?? 0

  const searchExpiryTimestamp = useMemo(() => {
    if (!searchResultsTimestamp) return 0
    return moment(searchResultsTimestamp).add(FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES, 'minutes').toDate().getTime()
  }, [searchResultsTimestamp])

  const expiryTimestamp = hasSearched ? searchExpiryTimestamp : initialExpiryTimestamp

  useEffect(() => {
    if (expiryTimestamp) {
      resetFlightsTimer(expiryTimestamp)
    }
  }, [expiryTimestamp])

  const handleSearchRef = useRef(handleSearch)
  handleSearchRef.current = handleSearch

  const isWidgetReady = !!flights[0]?.departureAirport?.airportCode &&
    !!flights[0]?.arrivalAirport?.airportCode &&
    !!flights[0]?.checkinDate

  useEffect(() => {
    if (fetching || !expiryTimestamp || !isWidgetReady) return
    if (hasFlightSessionExpired()) {
      handleSearchRef.current()
      return
    }
    const remaining = expiryTimestamp - Date.now()
    if (remaining <= 0) {
      handleSearchRef.current()
      return
    }
    const timeout = setTimeout(() => { handleSearchRef.current() }, Math.min(remaining, MAX_TIMEOUT_MS))
    return () => clearTimeout(timeout)
  }, [fetching, expiryTimestamp, isWidgetReady])

  const currentSegmentIndex = useMemo(() => segments.findIndex(s => s === activeSegmentKey), [segments, activeSegmentKey])

  const handleStepClick = useCallback<StepClickHandler>((stepIndex) => {
    setActiveSegmentKey(segments[stepIndex])
    const keysToRemove = segments.slice(stepIndex)
    setSelectedFlights(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
    setSelectedFareFamilies(prev => {
      const next = { ...prev }
      for (const key of keysToRemove) delete next[key]
      return next
    })
  }, [segments])

  const getStepSublabel = useCallback((journey: App.JourneyV2) => {
    const flight = journey.flightGroup.flights[0]
    const date = dateFormatter(flight.departureDateTime, INTL_DAY_MONTH_NAME)
    return `${date} · ${flight.departingTime}`
  }, [dateFormatter])

  const stepperItinerary = useMemo(() => {
    if (hasSearched && searchParams) return searchParams.itinerary
    const firstOutbound = outboundOptions[0]?.leg
    if (!firstOutbound) return []
    const legs = [{ departureAirport: firstOutbound.departureAirport, arrivalAirport: firstOutbound.arrivalAirport, departureDate: firstOutbound.departureDate }]
    const firstInbound = inboundOptions[0]?.leg
    if (isReturn && firstInbound) {
      legs.push({ departureAirport: firstInbound.departureAirport, arrivalAirport: firstInbound.arrivalAirport, departureDate: firstInbound.departureDate })
    }
    return legs
  }, [hasSearched, searchParams, outboundOptions, inboundOptions, isReturn])

  const buildFlightContext = useCallback((journey: App.JourneyV2) => {
    const jFlights = journey.flightGroup.flights
    const firstFlight = jFlights[0]
    const lastFlight = jFlights[jFlights.length - 1]
    const duration = journey.flightGroup.totalFlightDuration
    const lines = [
      `Carrier: ${journey.carrierName}`,
      `Flight: ${jFlights.map(f => f.flightNumber).filter(Boolean).join(' / ')}`,
      `From: ${firstFlight.departureAirport} (${firstFlight.departureAirportName})`,
      `To: ${lastFlight.arrivalAirport} (${lastFlight.arrivalAirportName})`,
      `Departure: ${firstFlight.departureDateTime}`,
      `Arrival: ${lastFlight.arrivalDateTime}`,
      `Duration: ${duration.hours}h ${duration.minutes}m`,
      `Stops: ${getTotalStopCount(jFlights)}`,
      `Price: ${journey.currencyCode} ${journey.price.adult.totalFare}`,
      `Fare type: ${journey.fareType}`,
      `Carrier logo: ${journey.carrierBrandMark ?? journey.carrierLogo}`,
    ]
    if (journey.bookingInfo?.journeyKey) lines.push(`Journey Key: ${journey.bookingInfo.journeyKey}`)
    if (journey.bookingInfo?.providerSearchId) lines.push(`Search ID: ${journey.bookingInfo.providerSearchId}`)
    return lines.join('\n')
  }, [])

  const handleSelectFlight = useCallback((selectData: { journey: App.JourneyV2 }) => {
    const idx = segments.findIndex(key => key === activeSegmentKey)

    if (idx < segments.length - 1) {
      setSelectedFlights(prev => ({ ...prev, [activeSegmentKey]: selectData.journey }))
      setActiveSegmentKey(segments[idx + 1])
      return
    }

    if (!onSendMessage || isStreaming) return

    const departingJourney = selectedFlights.departing
    const returningJourney = isReturn ? selectData.journey : undefined
    const outbound = departingJourney ?? selectData.journey
    const outFlights = outbound.flightGroup.flights
    const from = outFlights[0].departureAirport
    const to = outFlights[outFlights.length - 1].arrivalAirport

    const journeysToCache = isReturn && departingJourney && returningJourney
      ? [departingJourney, returningJourney]
      : [selectData.journey]
    cacheShortlistedJourneys(journeysToCache, data.searchId, data.sessionId)

    let userMessage: string
    let contextContent: string

    if (isReturn && departingJourney && returningJourney) {
      userMessage = intl.formatMessage(
        { id: 'ZE1Bw2jDpdHXdHmw312L', defaultMessage: 'Add return flight from {from} to {to}', description: 'User chat message shown when adding a return flight from the flight search drawer' },
        { from, to },
      )
      contextContent = `Departing flight:\n${buildFlightContext(departingJourney)}\n\nReturning flight:\n${buildFlightContext(returningJourney)}`
    } else {
      userMessage = intl.formatMessage(
        { id: 'mtc-4uBZ7D3tkBZV8ueR', defaultMessage: 'Add flight from {from} to {to}', description: 'User chat message shown when adding a one-way flight from the flight search drawer' },
        { from, to },
      )
      contextContent = buildFlightContext(selectData.journey)
    }

    const searchMeta = [
      `Search ID: ${data.searchId}`,
      `Search expiry: ${expiryTimestamp}`,
    ]
    if (hasSearched && searchParams) {
      searchMeta.push(`Search params: ${JSON.stringify(searchParams)}`)
    }

    const message = `${userMessage}\n<Context label="Flight search result">${contextContent}\n\n${searchMeta.join('\n')}</Context>`
    onSendMessage(message)
  }, [intl, segments, activeSegmentKey, selectedFlights, isReturn, onSendMessage, isStreaming, buildFlightContext, expiryTimestamp, hasSearched, searchParams, data.searchId, data.sessionId])

  const flightsHeading = intl.formatMessage({ id: 'Em8QS0fMuOnThg1GQ30N', defaultMessage: 'Flights', description: 'Default heading for the flight search results carousel in the trip planner chat' })

  const showLoading = hasSearched && fetching
  const showError = hasSearched && !fetching && !!error
  const showNoResults = hasSearched && !showLoading && !showError && displayJourneys.length === 0
  const showResults = !showLoading && !showError && displayJourneys.length > 0

  const drawerOrigin = flights[0]?.departureAirport
  const drawerDestination = flights[0]?.arrivalAirport

  const viewAllLabel = intl.formatMessage({
    id: 'Px4qk3r3kxjxEO_OcsHs',
    defaultMessage: 'View all',
    description: 'Button label to open the full flight search drawer from the chat flight results',
  })

  const isFlightListScrollable = displayJourneys.length > MAX_VISIBLE_FLIGHT_COUNT
  const visibleFlightCount = Math.min(displayJourneys.length, MAX_VISIBLE_FLIGHT_COUNT)

  const flightResultsMaxHeight = useMemo(() => {
    if (!showResults || !isFlightListScrollable) return undefined

    const cardHeight = flightCardHeight || FLIGHT_RESULTS_FALLBACK_CARD_HEIGHT
    return (visibleFlightCount * cardHeight) + ((visibleFlightCount - 1) * FLIGHT_RESULTS_GAP) - FLIGHT_RESULTS_SCROLL_PEEK_HEIGHT
  }, [showResults, isFlightListScrollable, flightCardHeight, visibleFlightCount])

  const firstDisplayJourneyId = displayJourneys[0]?.id

  useEffect(() => {
    if (!showResults) return

    const firstCard = flightResultsListRef.current?.firstElementChild as HTMLDivElement | null
    if (!firstCard) return

    const updateCardHeight = () => {
      setFlightCardHeight(firstCard.getBoundingClientRect().height)
    }

    updateCardHeight()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateCardHeight)
    resizeObserver.observe(firstCard)

    return () => {
      resizeObserver.disconnect()
    }
  }, [showResults, displayJourneys.length, firstDisplayJourneyId])

  return (
    <>
      <SearchWrapper>
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
            <Heading variant="heading4">{flightsHeading}</Heading>
            <TextButton kind="tertiary" size="medium" onClick={openDrawer}>
              {viewAllLabel}
            </TextButton>
          </Group>
          <FlightSearchWidgetWrapper>
            <FlightSearchWidgetDesktop onSearchAirport={handleSearch} wrapControls />
          </FlightSearchWidgetWrapper>
        </VerticalSpacer>
      </SearchWrapper>

      {isReturn && stepperItinerary.length > 1 && (
        <ContentWrapper>
          <StepperProgressSteps activeStepIndex={currentSegmentIndex}>
            {stepperItinerary.map((leg, index) => (
              <StepperProgressStep
                key={segments[index]}
                stepIndex={index}
                label={intl.formatMessage(
                  { id: 'AEiFGqvrHSc9lXaMfYat', defaultMessage: '{origin} — {destination}', description: 'Flight route step label showing origin and destination airport codes in trip planner' },
                  { origin: leg.departureAirport, destination: leg.arrivalAirport },
                )}
                onStepClick={selectedFlights[segments[index]] ? handleStepClick : undefined}
                sublabel={selectedFlights[segments[index]] ? getStepSublabel(selectedFlights[segments[index]]) : undefined}
              />
            ))}
          </StepperProgressSteps>
        </ContentWrapper>
      )}

      {showLoading && <>
        <FlightsLoadingTopBar />
        <ContentWrapper><LoadingAnimationBox variant="flights" /></ContentWrapper>
      </>}

      {showError && (
        <ContentWrapper>
          <BodyText variant="medium" colour="critical">
            {intl.formatMessage({ id: 'W6e3tDO59GW7SKXKCf3P', defaultMessage: 'Something went wrong while searching for flights. Please try again.', description: 'Error message when flight search fails in the trip planner drawer' })}
          </BodyText>
        </ContentWrapper>
      )}

      {showNoResults && (
        <ContentWrapper>
          <BodyText variant="medium" colour="light-foreground-alt">
            {intl.formatMessage({ id: 'P51jHD0yS8A8bb49PJWq', defaultMessage: 'No flights found for your search. Try different dates or airports.', description: 'Message shown when no flight results are found in the trip planner drawer' })}
          </BodyText>
        </ContentWrapper>
      )}

      {showResults && (
        <ContentWrapper>
          <VerticalSpacer gap={12}>
            <ChatFlightResultsHeader
              label={isReturningStep
                ? intl.formatMessage({ id: '4p1QhxNAMtAdkUW9nReN', defaultMessage: 'Returning flight', description: 'Label for returning flight segment in trip planner drawer' })
                : intl.formatMessage({ id: 'qJfCIiKntFNv9yNLHAHJ', defaultMessage: 'Departing flight', description: 'Label for departing flight segment in trip planner drawer' })}
              count={displayJourneys.length}
              sort={flightSort}
              originAirportCode={stepperItinerary[currentSegmentIndex]?.departureAirport}
              destinationAirportCode={stepperItinerary[currentSegmentIndex]?.arrivalAirport}
              onSortChange={setFlightSort}
            />
            <FlightResultsViewport
              $maxHeight={flightResultsMaxHeight}
              $isScrollable={isFlightListScrollable}
            >
              <FlightResultsList ref={flightResultsListRef}>
                {displayJourneys.map((journey) => (
                  <DrawerFlightCard
                    key={journey.id}
                    journey={journey}
                    onSelect={handleSelectFlight}
                    onViewDetails={() => showModal(<JourneyV2DetailsModal journey={journey} />)}
                    isAlreadyAdded={false}
                    alignColumns
                    responsiveCompact
                  />
                ))}
              </FlightResultsList>
            </FlightResultsViewport>
          </VerticalSpacer>
        </ContentWrapper>
      )}

      <FlightSearchDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        originAirportCode={drawerOrigin?.airportCode}
        originAirportName={drawerOrigin?.airportName}
        destinationAirportCode={drawerDestination?.airportCode}
        destinationAirportName={drawerDestination?.airportName}
        departureDate={departDate}
        returnDate={returnDate}
        fareType={fareType?.value}
        fareClass={fareCabin?.value}
        passengers={occupants?.adults}
        autoSearch
        onSendMessage={onSendMessage}
      />
    </>
  )
}

export default ChatFlightSearchContent
